import React from 'react';

import {
  PopupMenu,
  TextButton,
  Column,
  Tooltip,
  Icon,
} from 'design-system-web';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { routes } from 'lane-shared/config';
import useChannelAdminContext from 'hooks/useChannelAdminContext';

import { Invoice } from 'lane-shared/domains/billingPayments/types';
import { dates } from 'lane-shared/helpers/constants';
import { getInvoiceStatus } from 'lane-shared/helpers/features/getInvoiceStatus';
import { isInvoicePayable } from './isInvoicePayable';
import { dateFormatter } from 'lane-shared/helpers/formatters';
import { formatCurrencyWithSymbolOnly } from 'domains-billing-currency';
import {
  currencyToPaymentCurrency,
  graphQLToCurrency,
} from 'domains-billing-web-mappers';
import { Chip, ChipStyle } from 'components/ads';
import { isMonthlyInvoice } from './isMonthlyInvoice';

import {
  InvoiceActions,
  useGetActionsForInvoices,
} from './useGetActionsForInvoices';
import styles from '../styles.scss';
import useUserLocale from 'hooks/useUserLocale';

export function useGetInvoiceTableColumns(
  timeZone: string,
  threeLetterAbbrev?: string
): Column<Invoice>[] {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const channel_slug = channel?.slug || '';
  const invoiceActions = useGetActionsForInvoices();
  const locale = useUserLocale();

  const renderIdCell = (_: any, invoice: Invoice) => {
    const invoiceDisplayRoute = routes.channelAdminInvoiceView
      .replace(':id', channel_slug)
      .replace(':invoiceDisplayId', invoice.displayId);

    const isMonthly = isMonthlyInvoice(invoice);

    return (
      <span className={styles.inlineFlexCenter}>
        <Link to={invoiceDisplayRoute}>{invoice.displayId}</Link>
        {isMonthly && (
          <Tooltip
            TooltipComponent={t('abp.invoiceList.monthlyInvoice')}
            placement="top"
          >
            <span className={styles.calendarIconWrapper}>
              <Icon name="calendar" className={styles.calendarIcon} />
            </span>
          </Tooltip>
        )}
      </span>
    );
  };

  const invoiceColumns: Column<Invoice>[] = [
    {
      key: 'status',
      header: t('abp.invoiceList.status'),
      renderCell: renderStatusCell,
      renderForCSV: (cell, row) => {
        return t(renderStatusCellForCSV(cell, row));
      },
    },
    {
      key: 'id',
      header: t('abp.invoiceList.invoiceId'),
      renderCell: renderIdCell,
      renderForCSV: (_, row) => row.displayId,
    },
    { key: 'externalPayerId', header: t('abp.invoiceList.customer') },
    {
      key: 'currency',
      header: t('abp.chargeBillingReports.currency'),
      renderCell: renderCurrencyCell,
      renderForCSV: renderCurrencyCellForCSV,
    },
    {
      key: 'amount',
      header: t('abp.invoiceList.amount'),
      renderCell: (cell: number, row: Invoice) => renderAmountCell(cell, row, locale),
      renderForCSV: (cell: number, row: Invoice) => renderAmountCellForCSV(cell, row, locale),
    },
    {
      key: 'invoiceDate',
      header: `${t('abp.invoiceList.issueDate')}${
        threeLetterAbbrev ? ` ${threeLetterAbbrev}` : ''
      }`,
      renderCell: (cell: string) => renderDateCell(cell, timeZone),
      renderForCSV: (cell: string) => renderDateCellForCSV(cell, timeZone),
    },
    {
      key: 'dueDate',
      header: `${t('abp.invoiceList.dueDate')}${
        threeLetterAbbrev ? ` ${threeLetterAbbrev}` : ''
      }`,
      renderCell: (cell: string) => renderDateCell(cell, timeZone),
      renderForCSV: (cell: string) => renderDateCellForCSV(cell, timeZone),
    },
  ];

  if (invoiceActions.length) {
    invoiceColumns.push({
      key: 'actions',
      header: t('abp.actions'),
      renderCell: (cell: number, row: Invoice) =>
        renderActionsCell(cell, row, invoiceActions, t),
    });
  }

  return invoiceColumns;
}

function renderStatusCell(cell: string, invoiceDetails: Invoice) {
  const metadata = invoiceDetails.metadata || {};
  const statusMap = getInvoiceStatus(
    cell,
    invoiceDetails,
    metadata.autopay || false
  );
  const chipColor = statusMap[1] as ChipStyle;

  return <Chip value={statusMap[0]} type={chipColor} size="xs" />;
}

function renderStatusCellForCSV(cell: string, invoiceDetails: Invoice): string {
  const statusMap = getInvoiceStatus(cell, invoiceDetails);

  return statusMap[0];
}

function renderAmountCell(cell: number, invoiceDetails: Invoice, locale: string) {
  const currency = graphQLToCurrency(invoiceDetails.currency);

  return (
    <span>
      {formatCurrencyWithSymbolOnly(currency, cell, locale)}
    </span>
  );
}

function renderAmountCellForCSV(cell: number, invoiceDetails: Invoice, locale: string) {
  const currency = graphQLToCurrency(invoiceDetails.currency);

  return formatCurrencyWithSymbolOnly(currency, cell, locale);
}

function renderCurrencyCell(cell: number, invoiceDetails: Invoice) {
  return <span>{currencyToPaymentCurrency(graphQLToCurrency(invoiceDetails.currency))}</span>;
}

function renderCurrencyCellForCSV(cell: number, invoiceDetails: Invoice) {
  return currencyToPaymentCurrency(graphQLToCurrency(invoiceDetails.currency));
}

function renderDateCell(cell: string, timeZone: string) {
  return <span>{dateFormatter(cell, dates.SIMPLE_DATE, timeZone)}</span>;
}

function renderDateCellForCSV(cell: string, timeZone: string) {
  return dateFormatter(cell, dates.SIMPLE_DATE, timeZone);
}

function renderActionsCell(
  cell: number,
  row: Invoice,
  invoiceActions: InvoiceActions[],
  t: (key: string) => string
) {
  return (
    <PopupMenu
      trigger={
        <TextButton iconName="angle-down">{t('abp.actions')}</TextButton>
      }
      items={invoiceActions.map(
        ({ label: invoiceActionLabel, onSelect: onInvoiceActionSelected }) => {
          const triggerInvoiceAction = () => onInvoiceActionSelected(cell, row);

          return {
            label: invoiceActionLabel,
            onSelect: triggerInvoiceAction,
            isDisabled: !!(
              invoiceActionLabel === t('abp.actions.markAsPaid') &&
              !isInvoicePayable(row)
            ),
          };
        }
      )}
    />
  );
}
